/*
  Lista życzeń — podstrona.

  Buduje stronę z danych z `window.wishlist` (magazyn w przeglądarce) i wzorców
  markupu wypisanych przez sekcję, więc żaden napis nie jest zaszyty tutaj.
  Karty produktów dociąga alternatywnym szablonem karty motywu, dzięki czemu
  wyglądają identycznie jak na listingach kolekcji.

  Trzy stany strony pochodzą wprost z projektu: lista z produktami, lista pusta
  oraz widok bez zalogowania (bez akcji pod kartami).
*/

(() => {
  const root = document.querySelector("[data-wishlist]");
  if (!root || !window.wishlist) return;

  const store = window.wishlist;
  const main = document.getElementById("main");
  const grid = root.querySelector("[data-wishlist-grid]");
  const introBox = root.querySelector("[data-wishlist-intro]");
  const listsBox = root.querySelector("[data-wishlist-lists]");
  const optionsBox = root.querySelector("[data-wishlist-options]");

  const templates = new Map();
  document.querySelectorAll("[data-wishlist-template]").forEach((node) => {
    templates.set(node.dataset.wishlistTemplate, node);
  });

  const clone = (name) => {
    const template = templates.get(name);
    return template ? template.content.cloneNode(true) : null;
  };

  const isCustomer = Boolean(window.wishlistStrings && window.wishlistStrings.isCustomer);

  /* ---------- karta produktu z motywu ---------- */

  const cardCache = new Map();

  const fetchCard = (handle, variantId) => {
    const url = new URL(`/products/${handle}`, window.location.origin);
    url.searchParams.set("view", "card");
    if (variantId) url.searchParams.set("variant", variantId);
    const key = url.href;
    if (!cardCache.has(key)) {
      cardCache.set(
        key,
        fetch(key)
          .then((response) => (response.ok ? response.text() : ""))
          .catch(() => "")
      );
    }
    return cardCache.get(key);
  };

  /*
    Karta pobrana bez tkaniny pokazuje pierwszą z nich, a klient polubił
    konkretny kolor. Przestawiamy ją publiczną metodą karty - ta oczekuje
    wartości przepuszczonej przez jej własną normalizację, więc podajemy ją
    tak, jak robi to karta u siebie. Próbnika NIE klikamy: jest odnośnikiem
    i przeniósłby na kartę produktu.
  */
  const showFabric = (slot, fabric) => {
    const card = slot.querySelector("product-card");
    if (!card || typeof card.setSwatchImages !== "function") return;
    try {
      const value =
        typeof card.normalizeValue === "function" ? card.normalizeValue(fabric) : fabric;
      card.setSwatchImages(value);
    } catch (error) {
      console.warn("Nie udało się ustawić tkaniny na karcie listy życzeń", error);
    }
  };

  /*
    Produkt mógł zniknąć ze sklepu albo zmienić uchwyt, odkąd klient go polubił.
    Zamiast pustego miejsca pokazujemy wtedy zastępczy kafel — pozycja zostaje
    na liście, żeby klient sam zdecydował, czy ją usunąć.
  */
  const fillCard = async (item, element) => {
    const slot = element.querySelector("[data-wishlist-card]");
    if (!slot) return;
    const markup = item.h ? await fetchCard(item.h, item.v) : "";
    if (markup) {
      /*
        Karta pobrana bez tkaniny niesie zdjęcia i próbniki wszystkich tkanin
        produktu. Ustawiamy jej wybraną tkaninę PRZED wstawieniem do drzewa —
        element własny karty czyta ją przy podłączeniu i sam pokazuje właściwe
        zdjęcia oraz zapalony próbnik.
      */
      const box = document.createElement("div");
      box.innerHTML = markup;
      const card = box.querySelector("product-card");
      if (card && item.f) {
        card.dataset.initialOption = item.f;
        try {
          const context = JSON.parse(card.querySelector('[data-iwc-card-context]')?.textContent || '{}');
          if (context.context?.selection?.fabric_handle !== item.f) card.dataset.iwcContextUnresolved = 'true';
        } catch {
          card.dataset.iwcContextUnresolved = 'true';
        }
      }
      slot.innerHTML = "";
      while (box.firstChild) slot.appendChild(box.firstChild);
      if (item.f) showFabric(slot, item.f);
      return;
    }
    const fallback = clone("card-fallback");
    if (!fallback) return;
    slot.innerHTML = "";
    slot.appendChild(fallback);
    const link = slot.querySelector("[data-wishlist-fallback-link]");
    if (link && item.h) link.href = `/products/${item.h}`;
  };

  /* ---------- schowek ---------- */

  const showCopied = (badge) => {
    if (!badge) return;
    badge.hidden = false;
    clearTimeout(Number(badge.dataset.timer));
    badge.dataset.timer = String(
      setTimeout(() => {
        badge.hidden = true;
      }, 2000)
    );
  };

  const copyToClipboard = async (value, badge) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        const helper = document.createElement("textarea");
        helper.value = value;
        helper.setAttribute("readonly", "readonly");
        helper.style.cssText = "position:fixed;top:-1000px;opacity:0";
        document.body.appendChild(helper);
        helper.select();
        document.execCommand("copy");
        helper.remove();
      }
      showCopied(badge);
    } catch (error) {
      console.warn("Nie udało się skopiować odnośnika listy życzeń", error);
    }
  };

  const absoluteUrl = (path) => {
    try {
      return new URL(path, window.location.origin).href;
    } catch (error) {
      return path;
    }
  };

  /* ---------- koszyk ---------- */

  const addToCart = async (button, variantId) => {
    button.disabled = true;
    try {
      const items = [{ id: String(variantId), quantity: 1 }];
      const add = async () => {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items }),
        });
        if (!response.ok) throw new Error("cart");
        return response.json();
      };
      // The saved wishlist entry does not establish a complete configuration.
      // Confirmed native cart lines use the shared unresolved-context path.
      if (window.IWCAnalytics?.runCartOperation) {
        await window.IWCAnalytics.runCartOperation(add, null, { items });
      }
      else await add();
      // Motyw odświeża szufladę koszyka i licznik na tym zdarzeniu.
      document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    } catch (error) {
      console.warn("Nie udało się dodać produktu do koszyka", error);
    } finally {
      button.disabled = false;
    }
  };

  /* ---------- pozycje ---------- */

  const buildItem = (item) => {
    const fragment = clone("item");
    if (!fragment) return null;
    const element = fragment.querySelector("[data-wishlist-item]");
    if (!element) return null;
    element.dataset.variant = item.v;
    element.dataset.fabric = item.f || "";
    element.dataset.handle = item.h || "";

    fillCard(item, element);

    const remove = element.querySelector("[data-wishlist-remove]");
    if (remove) {
      remove.addEventListener("click", () => store.remove(item.v, item.f));
    }

    const actions = element.querySelector(".wishlist-card__actions");
    if (actions && !isCustomer) {
      // Projekt widoku bez zalogowania nie pokazuje akcji pod kartą.
      actions.hidden = true;
    }

    const cart = element.querySelector("[data-wishlist-add-to-cart]");
    if (cart) {
      cart.addEventListener("click", () => addToCart(cart, item.v));
    }

    const badge = element.querySelector("[data-wishlist-copied]");
    const share = element.querySelector("[data-wishlist-share-item]");
    if (share) {
      share.addEventListener("click", () => {
        const target = item.h ? `/products/${item.h}?variant=${item.v}` : window.location.href;
        copyToClipboard(absoluteUrl(target), badge);
      });
    }

    const moveButton = element.querySelector("[data-wishlist-move]");
    const movePanel = element.querySelector("[data-wishlist-move-panel]");
    const moveList = element.querySelector("[data-wishlist-move-list]");
    const others = store.lists().filter((list) => list.id !== store.activeListId());

    if (moveButton && movePanel && moveList && others.length > 0) {
      moveList.innerHTML = "";
      others.forEach((list) => {
        const row = document.createElement("li");
        const option = document.createElement("button");
        option.type = "button";
        option.className = "wishlist-card__move-option";
        option.textContent = list.name;
        option.addEventListener("click", () => {
          movePanel.hidden = true;
          moveButton.setAttribute("aria-expanded", "false");
          store.move(item.v, item.f, list.id);
        });
        row.appendChild(option);
        moveList.appendChild(row);
      });
      moveButton.hidden = false;
      moveButton.addEventListener("click", () => {
        const expanded = moveButton.getAttribute("aria-expanded") === "true";
        moveButton.setAttribute("aria-expanded", String(!expanded));
        movePanel.hidden = expanded;
      });
    }

    return element;
  };

  /* ---------- zakładki list ---------- */

  const renderLists = () => {
    const lists = store.lists();
    listsBox.innerHTML = "";

    if (lists.length > 1) {
      const active = store.activeListId();
      lists.forEach((list) => {
        const tab = document.createElement("button");
        tab.type = "button";
        tab.className = "wishlist__list-tab";
        tab.textContent = list.name;
        if (list.id === active) tab.setAttribute("aria-current", "true");
        tab.addEventListener("click", () => store.setActiveList(list.id));
        listsBox.appendChild(tab);
      });
    }

    /*
      Przycisk tworzenia listy pojawia się już przy jednej liście — inaczej
      klient nie miałby jak wyjść ze stanu jednej listy. Widzi go tylko osoba
      zalogowana, bo w projekcie zarządzanie listami należy do widoku po
      zalogowaniu.
    */
    if (!isCustomer) return;
    const create = clone("create");
    if (!create) return;
    listsBox.appendChild(create);
    const button = listsBox.querySelector("[data-wishlist-create]");
    if (!button) return;
    button.addEventListener("click", () => {
      const name = window.prompt(button.textContent.trim());
      if (name) store.createList(name);
    });
  };

  /* ---------- pasek opcji listy ---------- */

  const renderOptions = () => {
    optionsBox.innerHTML = "";
    // Projekt pokazuje opcje listy dopiero po zalogowaniu.
    if (!isCustomer) return;
    const fragment = clone("options");
    if (!fragment) return;
    optionsBox.appendChild(fragment);

    const badge = optionsBox.querySelector("[data-wishlist-copied]");
    const share = optionsBox.querySelector("[data-wishlist-share-list]");
    if (share) {
      share.addEventListener("click", () => copyToClipboard(absoluteUrl(window.location.pathname), badge));
    }

    const rename = optionsBox.querySelector("[data-wishlist-rename]");
    if (rename) {
      rename.addEventListener("click", () => {
        const current = store.lists().find((list) => list.id === store.activeListId());
        const name = window.prompt(rename.textContent.trim(), current ? current.name : "");
        if (name) store.renameList(store.activeListId(), name);
      });
    }

    const remove = optionsBox.querySelector("[data-wishlist-delete]");
    if (remove) {
      // Ostatniej listy nie da się usunąć — jest tożsama z samą listą życzeń.
      if (store.lists().length < 2) {
        remove.remove();
      } else {
        remove.addEventListener("click", () => {
          if (window.confirm(remove.textContent.trim() + "?")) {
            store.deleteList(store.activeListId());
          }
        });
      }
    }
  };

  /* ---------- pełne przerysowanie ---------- */

  const render = () => {
    const items = store.items();

    renderLists();
    renderOptions();

    grid.innerHTML = "";
    const empty = root.querySelector(".wishlist__empty");
    if (empty) empty.remove();

    if (items.length === 0) {
      // Pusta lista zastępuje w projekcie całą treść strony.
      if (introBox) introBox.hidden = true;
      listsBox.hidden = true;
      optionsBox.hidden = true;
      const fragment = clone("empty");
      if (fragment) root.appendChild(fragment);
      if (main) {
        main.classList.add("main--wishlist-empty");
        main.classList.remove("main--wishlist-guest");
      }
      return;
    }

    if (introBox) introBox.hidden = false;
    listsBox.hidden = listsBox.children.length === 0;
    optionsBox.hidden = optionsBox.children.length === 0;

    items.forEach((item) => {
      const element = buildItem(item);
      if (element) grid.appendChild(element);
    });

    if (main) {
      main.classList.remove("main--wishlist-empty");
      // Bez zakładek i paska opcji siatka schodzi niżej — jak w projekcie
      // widoku bez zalogowania.
      const hasHeaderRows = !listsBox.hidden || !optionsBox.hidden;
      main.classList.toggle("main--wishlist-guest", !hasHeaderRows);
    }
  };

  document.addEventListener("wishlist:change", render);
  render();

  document.addEventListener("click", (event) => {
    if (event.target.closest("[data-wishlist-move], [data-wishlist-move-panel]")) return;
    root.querySelectorAll("[data-wishlist-move-panel]").forEach((panel) => {
      panel.hidden = true;
    });
    root.querySelectorAll("[data-wishlist-move]").forEach((button) => {
      button.setAttribute("aria-expanded", "false");
    });
  });
})();
